import type { CSSProperties, MouseEvent } from 'react';
import photo from '../spandan.png';
import type { Colors } from '../theme';
import { LINKS, INTRO_LINES } from '../data';
import { scrollToId } from '../scroll';

interface Props {
  colors: Colors;
  mobile: boolean;
  sectionStyle: CSSProperties;
}

type QuickLink = {
  key: string;
  action: string;
  href: string;
  target?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
};

const QUICK_LINKS: QuickLink[] = [
  ...LINKS.map(l => ({ key: l.label, action: l.label, href: l.href, target: "_blank" })),
  {
    key: "contact",
    action: "cd ~/contact",
    href: "#contact",
    onClick: (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      scrollToId("contact");
    },
  },
];

function ChevronDownIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IntroText({ mobile, muted, accent }: { mobile: boolean; muted: string; accent: string }) {
  return (
    <p style={{
      margin: mobile ? "0 0 28px" : "0 0 34px",
      fontSize: mobile ? 22 : 29,
      lineHeight: 1.35,
      fontWeight: 400,
      display: "flex",
      flexDirection: "column",
      alignItems: mobile ? "center" : undefined,
      color: muted,
    }}>
      {INTRO_LINES.map((line) => (
        <span key={line.pre + line.hl}>
          {line.pre}<span style={{ color: accent, fontWeight: 700 }}>{line.hl}</span>{line.post}
        </span>
      ))}
    </p>
  );
}

function ProfileLinks({ mobile, colors }: { mobile: boolean; colors: Colors }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: mobile ? "center" : undefined }}>
      {QUICK_LINKS.map((l) => (
        <a key={l.key} className="pc-lk" href={l.href} target={l.target}
          rel={l.target ? "noopener noreferrer" : undefined} onClick={l.onClick}
          style={{
            display: "flex", alignItems: "baseline", gap: 8, textDecoration: "none",
            color: colors.fg, fontSize: mobile ? 13 : 14, width: "max-content",
          }}>
          <span style={{ color: colors.accent, fontWeight: 400 }}>$</span>
          <span style={{ color: colors.fg, fontWeight: 600, borderBottom: `1px dashed ${colors.hair}`, paddingBottom: 1 }}>
            {l.action}
          </span>
          <span className="pc-arw" aria-hidden="true">&rarr;</span>
        </a>
      ))}
    </div>
  );
}

export default function QuickLinksSection({ colors, mobile, sectionStyle }: Props) {
  const photoSize = mobile ? "min(70vw, 240px)" : "300px";

  return (
    <section id="quick-links" style={{ ...sectionStyle, position: "relative" }}>
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: mobile ? 24 : 40,
        width: "100%",
      }}>
        <h1 style={{
          margin: 0,
          fontWeight: 600,
          fontSize: mobile ? 26 : 34,
          letterSpacing: "-0.02em",
          textAlign: "center",
          animation: "pc-riseIn .7s cubic-bezier(.2,.7,.2,1) both",
        }}>
          Welcome to<br />/home/spandan
          <span style={{
            display: "inline-block",
            width: "0.5em",
            height: "1em",
            background: colors.fg,
            marginLeft: 2,
            verticalAlign: "-0.15em",
            animation: "cursor-blink 1s step-end infinite",
          }} />
        </h1>

        {mobile ? (
          <>
            <div style={{
              position: "relative", flex: "none", width: photoSize, height: photoSize,
              animation: "pc-riseIn .8s cubic-bezier(.2,.7,.2,1) both", animationDelay: ".12s",
            }}>
              <img src={photo} alt="Spandan Shrestha" style={{
                display: "block", width: photoSize, height: photoSize, objectFit: "cover",
                borderRadius: 6, boxShadow: colors.photoShadow, border: colors.photoBorder,
                transition: "box-shadow .35s ease, border-color .35s ease",
              }} />
            </div>
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", width: "100%",
              animation: "pc-riseIn .8s cubic-bezier(.2,.7,.2,1) both", animationDelay: ".24s",
            }}>
              <IntroText mobile muted={colors.muted} accent={colors.accent} />
              <ProfileLinks mobile colors={colors} />
            </div>
          </>
        ) : (
          // Tablet portrait (~768px) is not `mobile`, but 300px of photo beside a
          // 420px text column does not fit next to the contents rail — so the row
          // wraps to a stacked, centred layout instead of overflowing sideways.
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            flexWrap: "wrap", gap: "clamp(32px, 6vw, 72px)", width: "100%",
          }}>
            <div style={{
              position: "relative", flex: "none", width: 300, height: 300, maxWidth: "100%",
              animation: "pc-riseIn .8s cubic-bezier(.2,.7,.2,1) both", animationDelay: ".12s",
            }}>
              <img src={photo} alt="Spandan Shrestha" style={{
                display: "block", width: "100%", height: "100%", objectFit: "cover",
                borderRadius: 6, boxShadow: colors.photoShadow, border: colors.photoBorder,
                transition: "box-shadow .35s ease, border-color .35s ease",
              }} />
            </div>
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "flex-start",
              flex: "1 1 300px", minWidth: 0, maxWidth: 420,
              animation: "pc-riseIn .8s cubic-bezier(.2,.7,.2,1) both", animationDelay: ".24s",
            }}>
              <IntroText mobile={false} muted={colors.muted} accent={colors.accent} />
              <ProfileLinks mobile={false} colors={colors} />
            </div>
          </div>
        )}
      </div>

      {!mobile && (
        <div style={{
          position: "absolute",
          bottom: 28,
          left: "50%",
          animation: "pc-scrollHint 1.8s ease-in-out infinite",
          color: colors.muted,
          lineHeight: 1,
        }}>
          <ChevronDownIcon />
        </div>
      )}
    </section>
  );
}
